import React from "react";
import { MessageCircle, Clock, ShieldCheck } from "lucide-react";

const WHATSAPP_NUMBER = "447767954568"; // international format, no +, no leading 0
const WHATSAPP_DISPLAY = "+44 07767 954568";
const PREFILLED =
  "Hello Acnira, I'd like to chat about a project for my Manchester business.";
const WA_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(PREFILLED)}`;

export default function Whatsapp() {
  return (
    <section
      id="whatsapp"
      data-testid="whatsapp-section"
      className="relative py-24 md:py-28 bg-[#0a0a0a] border-y border-white/5 overflow-hidden"
    >
      {/* Glow */}
      <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-[#25D366]/10 blur-[120px] pointer-events-none"></div>
      <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-gold/10 blur-[120px] pointer-events-none"></div>

      <div className="relative mx-auto max-w-[1500px] px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-gold"></span>
              <span className="text-[11px] uppercase tracking-[0.32em] text-white/60">
                Direct line · WhatsApp
              </span>
            </div>
            <h2 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Prefer a quick chat?
              <br />
              <em className="gold-text-gradient not-italic">Message us</em> on
              WhatsApp.
            </h2>
            <p className="mt-6 text-white/60 leading-relaxed max-w-xl">
              Skip the form. Send a message and a senior strategist will reply within
              the hour during business hours — no chatbots, no call centres, just a
              real human in our Manchester studio.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3 text-sm text-white/65">
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-gold" />
                Mon–Fri · 9am–6pm
              </div>
              <span className="text-white/20">·</span>
              <div className="flex items-center gap-2">
                <ShieldCheck size={16} className="text-gold" />
                End-to-end encrypted
              </div>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-cta"
                className="shine inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-semibold bg-[#25D366] text-black hover:bg-[#2ee06f] transition-colors"
              >
                <MessageCircle size={18} strokeWidth={2.2} />
                Chat on WhatsApp
              </a>
              <a
                href={`tel:+${WHATSAPP_NUMBER}`}
                data-testid="whatsapp-call"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 text-sm font-semibold border border-white/20 text-white hover:border-gold/40 transition-colors"
              >
                Or call {WHATSAPP_DISPLAY}
              </a>
            </div>
          </div>

          {/* Mock chat card */}
          <div className="lg:col-span-5">
            <div className="glass-strong p-6 md:p-8 max-w-md ml-auto" data-testid="whatsapp-preview">
              <div className="flex items-center gap-3 pb-4 border-b border-white/10">
                <div className="h-11 w-11 rounded-full gold-bg-gradient flex items-center justify-center text-black font-bold">
                  A
                </div>
                <div>
                  <div className="text-white text-sm">Acnira Studio</div>
                  <div className="flex items-center gap-1.5 text-[11px] text-[#25D366]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#25D366] animate-pulse"></span>
                    online · typically replies in &lt; 1h
                  </div>
                </div>
                <MessageCircle size={18} className="ml-auto text-[#25D366]" />
              </div>

              <div className="mt-5 space-y-3">
                <div className="ml-auto max-w-[80%] bg-[#054640] text-white text-sm px-4 py-2.5 rounded-2xl rounded-tr-sm">
                  Hi, I run a salon in Didsbury — looking for a new website.
                </div>
                <div className="text-[10px] text-white/40 text-right pr-1">11:42 ✓✓</div>

                <div className="max-w-[80%] bg-white/8 text-white text-sm px-4 py-2.5 rounded-2xl rounded-tl-sm">
                  Lovely — happy to help. Are you free for a 15‑min call this week?
                </div>
                <div className="text-[10px] text-white/40 pl-1">11:43</div>

                <div className="ml-auto max-w-[80%] bg-[#054640] text-white text-sm px-4 py-2.5 rounded-2xl rounded-tr-sm">
                  Yes, Thursday 2pm works.
                </div>
                <div className="text-[10px] text-white/40 text-right pr-1">11:44 ✓✓</div>
              </div>

              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 flex items-center justify-center gap-2 w-full py-3 border border-[#25D366]/40 text-[#25D366] text-sm font-medium hover:bg-[#25D366]/10 transition-colors"
              >
                Continue this conversation →
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
